import { SavedLayoutModel } from 'store/feature/layoutsSlice/models';
import cn from 'classnames';
import './LayoutManagerPanel.scss';
import StarFilledIcon from '_legacy/components/icons/StarFilledIcon';
import SlideoutButton from '_legacy/components/SlideoutButton';
import { Trash } from '_legacy/components/icons/Trash';
import StarOutlineIcon from '_legacy/components/icons/StarOutlineIcon';
import React, { useState } from 'react';
import LayoutManagerItemLabel from './LayoutManagerItemLabel';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { userSelectors } from 'store/feature/userSlice';
import {
    deleteTemplateLayout,
    deleteUserLayout,
    layoutsSelectors,
    setUserLayoutAsPreferred,
    toggleLayoutGroupPin,
} from 'store/feature/layoutsSlice';
import LayoutIcons from 'components/common/LayoutIcons';
import DeleteTemplateLayoutConfirmationDialog from '../dialogs/DeleteTemplateLayoutConfirmationDialog';
import useGridContext from 'hooks/grid/useGridContext';
import {
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tooltip,
} from '@mui/material';
import PushPin from '@mui/icons-material/PushPin';
import PushPinOutlined from '@mui/icons-material/PushPinOutlined';

const LayoutManagerDeleteHandle: React.FC<{ model: SavedLayoutModel }> = ({
    model,
}) => {
    const dispatch = useAppDispatch();
    const { gridType } = useGridContext();
    const loading = useAppSelector(layoutsSelectors.loading);
    const isPinnedToGroup = (model.preferredForGroupIds ?? []).length > 0;
    const disabled = loading || model.isPreferred || isPinnedToGroup;

    const disabledReason = isPinnedToGroup
        ? 'Unset as Group Default before deleting'
        : undefined;

    const [showConfirmation, setShowConfirmation] = useState(false);

    const handleDeleteTemplateLayout = () => {
        dispatch(deleteTemplateLayout({ id: model.id, gridType }));
        setShowConfirmation(false);
    };

    const handleDeleteLayout = () => {
        if (disabled) {
            return;
        }

        if (model.isTemplate) {
            setShowConfirmation(true);
        } else {
            dispatch(deleteUserLayout({ id: model.id, gridType }));
        }
    };

    return (
        <>
            <Tooltip
                title={disabledReason ?? ''}
                placement="top"
                arrow
                disableHoverListener={!disabledReason}
            >
                <span>
                    <SlideoutButton
                        data-cy="layout-delete-prefferd"
                        className="layoutManager-delete-layout"
                        onClick={handleDeleteLayout}
                        icon={Trash}
                        style={
                            disabled
                                ? { 'pointer-events': 'none', opacity: '0.5' }
                                : {}
                        }
                    />
                </span>
            </Tooltip>
            <DeleteTemplateLayoutConfirmationDialog
                open={showConfirmation}
                onCancel={() => setShowConfirmation(false)}
                onDelete={handleDeleteTemplateLayout}
            />
        </>
    );
};

const LayoutManagerPreferredHandle: React.FC<{ model: SavedLayoutModel }> = ({
    model,
}) => {
    const dispatch = useAppDispatch();
    const loading = useAppSelector(layoutsSelectors.loading);
    const { gridType } = useGridContext();
    const handleSetPreferred = () => {
        if (model.isTemplate || loading) {
            return;
        }
        dispatch(setUserLayoutAsPreferred({ id: model.id, gridType }));
    };

    return model.isPreferred ? (
        <StarFilledIcon
            className={cn({ 'disabled-icon': loading })}
            data-cy="layout-preferred"
        />
    ) : (
        <StarOutlineIcon
            className={cn({ 'disabled-icon': loading })}
            data-cy="layout-not-preferred"
            onClick={handleSetPreferred}
        />
    );
};

const LayoutManagerGroupPinHandle: React.FC<{ model: SavedLayoutModel }> = ({
    model,
}) => {
    const dispatch = useAppDispatch();
    const loading = useAppSelector(layoutsSelectors.loading);
    const { gridType } = useGridContext();
    const allGroups = useAppSelector(userSelectors.userGroups);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    if (model.isTemplate || allGroups.length < 2) return null;

    const pinnedGroupIds = model.preferredForGroupIds ?? [];
    const pinnedGroups = allGroups.filter(
        (g) => pinnedGroupIds.indexOf(g.id) !== -1
    );

    const tooltipTitle =
        pinnedGroups.length === 0
            ? 'Pin to group — click to configure'
            : `Pinned to: ${pinnedGroups.map((g) => g.name).join(', ')}`;

    const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };
    const handleClose = () => setAnchorEl(null);

    const handleToggle = (groupId: string) => {
        if (loading) return;
        dispatch(
            toggleLayoutGroupPin({ layoutId: model.id, groupId, gridType })
        );
    };

    return (
        <>
            <Tooltip title={tooltipTitle} placement="top" arrow>
                <span
                    className={cn({ 'disabled-icon': loading })}
                    onClick={handleOpen}
                    data-cy="layout-group-pin"
                    style={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    {pinnedGroups.length > 0 ? (
                        <PushPin fontSize="small" />
                    ) : (
                        <PushPinOutlined fontSize="small" />
                    )}
                </span>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
            >
                {allGroups.map((group) => {
                    const isPinned = pinnedGroupIds.indexOf(group.id) !== -1;
                    return (
                        <MenuItem
                            key={group.id}
                            onClick={() => handleToggle(group.id)}
                            dense
                        >
                            <ListItemIcon>
                                {isPinned ? (
                                    <PushPin fontSize="small" />
                                ) : (
                                    <PushPinOutlined fontSize="small" />
                                )}
                            </ListItemIcon>
                            <ListItemText>{group.name}</ListItemText>
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
};

export interface LayoutManagerItemProps {
    model: SavedLayoutModel;
}

const LayoutManagerItem: React.FC<LayoutManagerItemProps> = ({ model }) => {
    const isAdmin = useAppSelector(userSelectors.isAdmin);

    const [showEditHandle, setShowEditHandle] = useState(false);

    const canEdit = !model.isTemplate || isAdmin;
    const icon = model.isTemplate ? (
        <LayoutIcons.Template />
    ) : (
        <LayoutIcons.User />
    );

    return (
        <tr
            key={model.id}
            className={cn('layoutManager-item', {
                'layoutManager-item--preferred': model.isPreferred,
                'layoutManager-preferred': model.isPreferred,
            })}
        >
            <td>
                <div className="layoutManager-table-cell">{icon}</div>
            </td>
            <td
                onMouseOver={() => setShowEditHandle(canEdit && true)}
                onMouseLeave={() => setShowEditHandle(canEdit && false)}
            >
                <LayoutManagerItemLabel
                    model={model}
                    showControls={showEditHandle}
                />
            </td>
            <td>
                <div className="layoutManager-table-cell layoutManager-actions">
                    {!model.isTemplate ? (
                        <LayoutManagerPreferredHandle model={model} />
                    ) : (
                        <div></div>
                    )}
                    {!model.isTemplate ? (
                        <LayoutManagerGroupPinHandle model={model} />
                    ) : (
                        <div></div>
                    )}
                    {canEdit ? (
                        <LayoutManagerDeleteHandle model={model} />
                    ) : (
                        <div></div>
                    )}
                </div>
            </td>
        </tr>
    );
};

export default LayoutManagerItem;
